interface DownloadFileOptions {
  /** Full URL to fetch the file from */
  fileUrl: string;
  /** Desired filename the user will see when saving */
  fileName: string;
  /** MIME type hint for the Accept header and blob (e.g. 'text/calendar', 'application/pdf') */
  mimeType?: string;
  /** Optional URL to redirect to after a successful download */
  redirectUrl?: string;
  /** Delay in ms before redirecting (default: 2500) */
  redirectDelay?: number;
}

/**
 * Downloads a file via fetch, creates a temporary blob URL, and triggers a
 * browser save dialog with a user-friendly filename.
 *
 * Falls back to a direct `<a download>` link if the fetch-based approach fails
 * (e.g. due to CORS or network issues).
 */
export async function downloadFile(options: DownloadFileOptions): Promise<void> {
  const { fileUrl, fileName, mimeType, redirectUrl, redirectDelay = 2500 } = options;

  try {
    const headers: Record<string, string> = {};
    if (mimeType) {
      headers['Accept'] = `${mimeType},*/*`;
    }

    const response = await fetch(fileUrl, { headers });
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);

    const blob = await response.blob();
    // Some servers don't set the Content-Type correctly — ensure the blob has a type
    const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType || 'application/octet-stream' });

    const url = window.URL.createObjectURL(typedBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    if (redirectUrl) {
      setTimeout(() => {
        window.location.href = redirectUrl;
      }, redirectDelay);
    }
  } catch (error) {
    console.error('Download error:', error);
    // Fallback: create a direct download link and let the browser handle it
    const tempLink = document.createElement('a');
    tempLink.href = fileUrl;
    tempLink.setAttribute('download', fileName);
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
  }
}
